import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import defaultAvatar from '../assets/svg/images/default-avatar.jpg';
import './WelcomePage.css';

const WelcomePage = () => {
  const [showAuthButtons, setShowAuthButtons] = useState(false);
  const [latestUsers, setLatestUsers] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const navigate = useNavigate();

  // Fetch the latest 10 users from Firebase
  useEffect(() => {
    const fetchLatestUsers = async () => {
      try {
        const usersQuery = query(
          collection(db, 'users'),
          orderBy('timestamp', 'desc'),
          limit(10)
        );
        const querySnapshot = await getDocs(usersQuery);
        const users = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setLatestUsers(users);
      } catch (error) {
        console.error('Error fetching latest users:', error);
      }
    };

    fetchLatestUsers();
  }, []);

  const handleGetStarted = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setShowAuthButtons(prev => !prev); // Toggle instead of just setting to true
      setIsAnimating(false);
    }, 200); // Reduced animation time for better UX
  };

  const handlePolicyClick = (section = '') => {
    navigate(`/terms-of-service${section ? `#${section}` : ''}`);
  };

  return (
    <div className="welcome-page text-white min-h-screen overflow-hidden relative">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-gradient-to-r from-pink-500 to-yellow-500 opacity-10"
            style={{
              width: `${Math.random() * 10 + 5}px`,
              height: `${Math.random() * 10 + 5}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `float ${Math.random() * 15 + 10}s linear infinite`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-2 py-12 flex flex-col items-center relative z-10">
        {/* Main Header */}
        <h1 className="main-header text-4xl md:text-6xl lg:text-7xl font-bold text-center mb-6 bg-gradient-to-r from-pink-500 via-orange-500 to-yellow-500 bg-clip-text text-transparent animate-gradient-x">
          Welcome to AweitFlix – Where Movies Meet the Moment!
        </h1>

        {/* Title */}
        <p className="title text-xl md:text-2xl text-center mb-8 max-w-3xl leading-relaxed">
          We aren't just a Movie haven, we are the whole vibe, a living, breathing universe of stories, emotions, and connections.
        </p>

        {/* Get Started Button */}
        <div className={`transition-all duration-300 ease-out ${showAuthButtons ? 'scale-90' : 'scale-100'}`}>
          <button
            onClick={handleGetStarted}
            disabled={isAnimating}
            className={`get-started-button px-8 py-4 rounded-full text-white text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-300 ease-in-out bg-gradient-to-r from-pink-500 via-orange-500 to-yellow-500 bg-size-200 hover:bg-size-100 ${isAnimating ? 'transform scale-95' : ''}`}
          >
            {isAnimating ? '...' : showAuthButtons ? 'CLOSE' : 'GET STARTED'}
          </button>
        </div>

        {/* Auth Buttons (Pop-out) */}
        <div className={`auth-buttons-container transition-all duration-300 ease-in-out ${showAuthButtons ? 'max-h-40 opacity-100 mt-8' : 'max-h-0 opacity-0'}`}>
          <div className="flex flex-col items-center gap-4">
            <button
              onClick={() => navigate('/sign-up')}
              className="sign-up-button px-8 py-3 rounded-full text-white text-lg font-medium shadow-lg hover:shadow-xl transition duration-150 ease-in-out bg-gradient-to-r from-pink-500 to-orange-500"
            >
              Sign Up
            </button>
            <button
              onClick={() => navigate('/sign-in')}
              className="sign-in-button px-8 py-3 rounded-full bg-white text-black text-lg font-medium shadow-lg hover:bg-gray-200 transition duration-150 ease-in-out"
            >
              Sign In
            </button>
          </div>
        </div>

        {/* Orbiting User Profiles */}
        <div className="orbit-container relative w-full max-w-2xl h-96 my-16 mx-auto">
          {/* Orbit paths */}
          <div className="orbit-path orbit-1"></div>
          <div className="orbit-path orbit-2"></div>
          <div className="orbit-path orbit-3"></div>
          
          {/* User avatars */}
          {latestUsers.map((user, index) => (
            <div
              key={user.id}
              className={`user-profile absolute rounded-full border-2 border-white transition-all duration-1000 ease-in-out`}
              style={{
                width: '3.5rem',
                height: '3.5rem',
                transform: `rotate(${(360 / latestUsers.length) * index}deg) translateX(8rem) rotate(-${(360 / latestUsers.length) * index}deg)`,
                animation: `orbit-${index % 3 + 1} ${15 + (index % 5)}s linear infinite`,
                animationDelay: `${index * 0.5}s`,
              }}
            >
              <img
                src={user.photoURL || defaultAvatar}
                alt={user.name || 'User'}
                className="w-full h-full rounded-full object-cover"
              />
            </div>
          ))}
        </div>

        {/* Policy Links */}
        <div className="policy-links text-center text-sm text-gray-400 mt-12">
          By continuing, you agree to our{' '}
          <button
            onClick={() => handlePolicyClick('user-policy')}
            className="text-pink-500 hover:text-pink-400 cursor-pointer underline transition duration-150"
          >
            User policy
          </button>
          ,{' '}
          <button
            onClick={() => handlePolicyClick('privacy-policy')}
            className="text-pink-500 hover:text-pink-400 cursor-pointer underline transition duration-150"
          >
            Privacy policy
          </button>{' '}
          and{' '}
          <button
            onClick={() => handlePolicyClick('terms-of-service')}
            className="text-pink-500 hover:text-pink-400 cursor-pointer underline transition duration-150"
          >
            Terms of service
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomePage;